import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { IOffersFragment } from 'generated/graphql-gateway';
import { useLoyaltyEngineRewards } from 'hooks/use-loyalty-engine-rewards';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  IncentiveEvaluationMap,
  LoyaltyOfferWithInlineAlertMessage,
} from 'state/loyalty/hooks/types';
import {
  compareBy_idAndErrors,
  createListComparator,
  useConditionalState,
} from 'state/loyalty/hooks/use-conditional-state';
import {
  getWalmartOfferUnavailableMessage,
  isWalmartOffer,
} from 'state/loyalty/hooks/utils/walmart';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { LoyaltyOffer, LoyaltyOfferGroup } from 'state/loyalty/types';
import { useOrderContext } from 'state/order';

import { useLoyaltyIncentivesAvailabilityUtils } from '../loyalty-incentives-components/use-loyalty-incentives-availability';

import { useLoyaltyOffersFilters } from './use-loyalty-offers-filters';
import { reduceOffers } from './utils';

interface UseReduceOffersProps {
  cmsOffers: LoyaltyOffer[];
  cmsOfferGroups: LoyaltyOfferGroup[];
  personalizedOffers: LoyaltyOffer[];
  baseOffers: IOffersFragment[];
  offersLoading: boolean;
  refreshOffersLoading?: boolean;
}

const compareBy_idAndOffers = createListComparator(['_id', 'offers']);

export function useReduceOffers({
  cmsOffers,
  cmsOfferGroups,
  personalizedOffers,
  baseOffers,
  offersLoading,
  refreshOffersLoading = false,
}: UseReduceOffersProps) {
  const formatters = useIntl();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const { inRestaurantRedemptionCart } = useInRestaurantRedemptionContext();
  const {
    checkLoyaltyOfferAvailability,
    getIncentiveUnavailableMessage,
  } = useLoyaltyIncentivesAvailabilityUtils();
  const { engineRewardsMap } = useLoyaltyEngineRewards();

  const enableSortOffersForServiceMode = useFlag(
    LaunchDarklyFlag.ENABLE_SORT_OFFERS_FOR_SERVICE_MODE
  );
  const { appliedFilters } = useLoyaltyOffersFilters();
  const dispatch = useAppDispatch();

  // @TODO: We could think about default to null values instead of emtpty arrays here
  const [offers, setOffers] = useConditionalState(
    [] as LoyaltyOfferWithInlineAlertMessage[],
    compareBy_idAndErrors
  );
  const [offersInCart, setOffersInCart] = useConditionalState(
    [] as LoyaltyOffer[],
    compareBy_idAndErrors
  );
  const [offerGroups, setOfferGroups] = useConditionalState(
    [] as LoyaltyOfferGroup[],
    compareBy_idAndOffers
  );

  const [reductionLoading, setReductionLoading] = useState<boolean>(true);

  const { serviceMode } = useOrderContext();

  const runOffersReduction = useCallback(() => {
    if (!cmsOffers?.length || offersLoading || refreshOffersLoading) {
      return;
    }

    setReductionLoading(true);

    try {
      const [_offers, _offersInCart, _offerGroups] = reduceOffers({
        cmsOffers,
        cmsOfferGroups,
        personalizedOffers,
        baseOffers,
        appliedOffers,
        inRestaurantRedemptionCart,
        checkLoyaltyOfferAvailability,
        getIncentiveUnavailableMessage: (incentive: LoyaltyOffer) => {
          const loyaltyEngineId = incentive.loyaltyEngineId;
          if (!loyaltyEngineId) {
            return;
          }

          // Override Walmart offer max redemption availability error with custom message
          if (isWalmartOffer(incentive)) {
            if (incentive.errors && incentive.errors.length) {
              if (
                incentive.errors.find(
                  (error: { code: string }) => error.code === 'exceeds-max-redemption'
                )
              ) {
                return getWalmartOfferUnavailableMessage(incentive._id, formatters);
              }
            }
          }

          return getIncentiveUnavailableMessage({
            incentive,
            pointCost: engineRewardsMap[loyaltyEngineId]?.pointCost,
            locked: engineRewardsMap[loyaltyEngineId]?.locked,
          });
        },
        enableSortOffersForServiceMode,
        // @ts-expect-error TS(2322) FIXME: Type '(offerFeedbackMap: IncentiveEvaluationMap) =... Remove this comment to see the full error message
        setOffersFeedbackMap: (offerFeedbackMap: IncentiveEvaluationMap) =>
          dispatch(actions.loyalty.setOffersFeedbackMap(offerFeedbackMap)),
        appliedFilters,
        selectedServiceMode: serviceMode,
        formatters,
      });

      setOffers(_offers);
      setOffersInCart(_offersInCart);
      setOfferGroups(_offerGroups);
    } catch (e) {
      // If the offers reduce fails, we ensure the user will not see and endless loading screen,
      // and instead sees the offers empty screens
      setReductionLoading(false);
    } finally {
      setReductionLoading(false);
    }
  }, [
    cmsOffers,
    offersLoading,
    refreshOffersLoading,
    cmsOfferGroups,
    personalizedOffers,
    baseOffers,
    appliedOffers,
    inRestaurantRedemptionCart,
    checkLoyaltyOfferAvailability,
    enableSortOffersForServiceMode,
    appliedFilters,
    serviceMode,
    formatters,
    setOffers,
    setOffersInCart,
    setOfferGroups,
    getIncentiveUnavailableMessage,
    engineRewardsMap,
    dispatch,
  ]);

  // @TODO: We should review this, how we trigger the offers reduction
  useEffect(() => {
    runOffersReduction();
  }, [runOffersReduction]);

  return {
    offers,
    offersInCart,
    offerGroups,
    reductionLoading,
    reprocessOffers: runOffersReduction,
  };
}
